import { FunctionComponent, ReactNode, useState } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

interface OutreachSectionProps {
	/** Section heading, e.g. "Campaigns". */
	title: string;
	/** Optional one-line explanation under the heading. */
	subtitle?: string;
	/** Controls rendered at the right of the header, before the chevron (e.g. the campaign buttons). */
	headerActions?: ReactNode;
	/** Whether the section starts expanded. All three step sections start open. */
	defaultOpen?: boolean;
	children: ReactNode;
}

/**
 * A collapsible box for one section of the list wizard's Outreach step (General / Campaigns / Chat,
 * PREC-3687). The shared `Accordion` renders a single-open list of plain title rows, so it cannot
 * host per-section header actions — this is the step's own box.
 */
export const OutreachSection: FunctionComponent<OutreachSectionProps> = ({
	title,
	subtitle,
	headerActions,
	defaultOpen = true,
	children,
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

	const toggle = () => setIsOpen((open) => !open);

	return (
		<section className="mb-4 w-full rounded-xl border border-border-light bg-white">
			<div className="flex items-center justify-between gap-4 px-5 py-4">
				<button
					aria-expanded={isOpen}
					className="flex min-w-0 flex-1 cursor-pointer flex-col items-start text-left"
					type="button"
					onClick={toggle}
				>
					<h2 className="text-sm font-bold text-text">{title}</h2>
					{subtitle ? <p className="mt-0.5 text-xs text-text-meta">{subtitle}</p> : null}
				</button>
				<div className="flex shrink-0 items-center gap-3">
					{headerActions}
					<button
						aria-expanded={isOpen}
						aria-label={title}
						className="cursor-pointer text-text-meta hover:text-brand-light"
						type="button"
						onClick={toggle}
					>
						<Icon className="h-4 w-4" icon={isOpen ? faChevronUp : faChevronDown} />
					</button>
				</div>
			</div>
			{isOpen ? <div className="px-5 pb-5">{children}</div> : null}
		</section>
	);
};
